import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { isTreeActive, pathMatches } from "./alignedMenuConfig";

const DisabledItem = ({ label, nested }) => (
  <li className="menu-item-disabled">
    <span
      className={`menu-item-disabled-link${nested ? " nested" : ""}`}
      title="Coming soon"
      aria-disabled="true"
    >
      {nested ? <i className="bi bi-circle"></i> : null}
      <span className="menu-title text-muted">{label}</span>
      <span className="menu-coming-soon">Soon</span>
    </span>
  </li>
);

const LeafLink = ({ item, pathname, nested, onNavigate }) => {
  const active = pathMatches(pathname, item.path);
  const icon = nested ? <i className="bi bi-circle"></i> : null;
  const title = nested ? (
    <>
      {icon}
      {item.label}
    </>
  ) : (
    <>
      <div className="parent-icon">
        <i className="bi bi-circle"></i>
      </div>
      <div className="menu-title">{item.label}</div>
    </>
  );

  if (item.openInNewTab) {
    return (
      <li className={active ? "mm-active" : ""}>
        <a href={item.path} target="_blank" rel="noopener noreferrer">
          {title}
        </a>
      </li>
    );
  }

  if (!item.path) {
    return (
      <li className="menu-item-disabled">
        <span className="menu-item-disabled-link nested">
          {nested ? <i className="bi bi-circle"></i> : null}
          <span className="menu-title text-muted">{item.label}</span>
        </span>
      </li>
    );
  }

  return (
    <li className={active ? "mm-active" : ""}>
      <Link
        to={item.path}
        className={active && nested ? "active" : undefined}
        onClick={onNavigate}
      >
        {title}
      </Link>
    </li>
  );
};

/** Click-to-expand parent; also auto-opens when a child route is active. */
function useMenuOpen(pathOpen, resetKey) {
  const [manualOpen, setManualOpen] = useState(null);

  useEffect(() => {
    setManualOpen(null);
  }, [resetKey]);

  const open = manualOpen === null ? pathOpen : manualOpen;

  const setOpen = (next) => {
    setManualOpen(next);
  };

  const toggle = () => {
    setManualOpen(!(manualOpen === null ? pathOpen : manualOpen));
  };

  return { open, toggle, setOpen };
}

const MenuGroup = ({ group, pathname, depth = 0, onNavigate }) => {
  const pathOpen = isTreeActive(group.children, pathname);
  const { open, toggle } = useMenuOpen(pathOpen, pathname);
  const groupKey = group.item_key || group.label;

  return (
    <li className={open ? "mm-active" : ""}>
      <a
        href="#menu-group"
        className="has-arrow"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          toggle();
        }}
        aria-expanded={open}
        role="button"
      >
        {depth > 0 ? (
          <>
            <i className="bi bi-circle"></i>
            {group.label}
          </>
        ) : (
          <>
            <div className="parent-icon">
              <i className="bi bi-folder2"></i>
            </div>
            <div className="menu-title">{group.label}</div>
          </>
        )}
      </a>
      <ul className={open ? "mm-show" : "mm-collapse"}>
        {(group.children || []).map((child, idx) => (
          <MenuNode
            key={`${groupKey}-${child.label || child.kind}-${idx}`}
            item={child}
            pathname={pathname}
            depth={depth + 1}
            onNavigate={onNavigate}
          />
        ))}
      </ul>
    </li>
  );
};

const MenuNode = ({ item, pathname, depth = 0, onNavigate }) => {
  if (item.kind === "label") {
    return (
      <li className="menu-section-label px-3 py-1 text-muted small">
        {item.label}
      </li>
    );
  }
  if (item.kind === "group") {
    return (
      <MenuGroup
        group={item}
        pathname={pathname}
        depth={depth}
        onNavigate={onNavigate}
      />
    );
  }
  if (item.enabled === false) {
    return <DisabledItem label={item.label} nested={depth > 0} />;
  }
  return (
    <LeafLink
      item={item}
      pathname={pathname}
      nested={depth > 0}
      onNavigate={onNavigate}
    />
  );
};

export const AlignedModule = ({ module, pathname, onNavigate }) => {
  const pathOpen = isTreeActive(module.children, pathname);
  const moduleId = module.id || module.item_key || module.label;
  const { open, toggle } = useMenuOpen(pathOpen, pathname);

  const onHeaderClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    toggle();
  };

  return (
    <li className={open ? "mm-active" : ""}>
      <a
        href="#menu-module"
        className="has-arrow"
        onClick={onHeaderClick}
        aria-expanded={open}
        role="button"
      >
        <div className="parent-icon">
          <i className={`bi ${module.icon || "bi-circle"}`}></i>
        </div>
        <div className="menu-title">{module.label}</div>
      </a>
      <ul className={open ? "mm-show" : "mm-collapse"}>
        {(module.children || []).map((child, idx) => (
          <MenuNode
            key={`${moduleId}-${child.label || child.kind}-${idx}`}
            item={child}
            pathname={pathname}
            depth={1}
            onNavigate={onNavigate}
          />
        ))}
      </ul>
    </li>
  );
};

export const AlignedMenuItems = ({ modules, pathname, onNavigate }) =>
  (modules || []).map((mod, idx) => (
    <AlignedModule
      key={mod.id || mod.item_key || `mod-${idx}`}
      module={mod}
      pathname={pathname}
      onNavigate={onNavigate}
    />
  ));

export default AlignedMenuItems;
